import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X } from 'lucide-react';
import { filterByQuery } from '@/util/turkishSearch';
import { tr } from '@/i18n/tr';
import styles from './searchSelect.module.scss';

export interface SearchSelectItem {
  id: string;
  name: string;
  /** Shown before the name — a <ProduceIcon> for products. */
  icon?: ReactNode;
  /** Shown last, never filtered by a search, and held back while rows are hidden. */
  pinned?: boolean;
}

export function visibleItems(
  items: readonly SearchSelectItem[],
  query: string,
  canSearch: boolean,
  initialCount: number | undefined,
  expanded: boolean
): { visible: SearchSelectItem[]; hiddenCount: number } {
  const pinned = items.filter((i) => i.pinned);
  const rest = items.filter((i) => !i.pinned);
  const matches = canSearch ? filterByQuery(rest, query) : rest;
  // A search already narrows the list; truncating it would hide the row typed for.
  const truncating = initialCount !== undefined && query.trim() === '' && !expanded;
  const shown = truncating ? matches.slice(0, initialCount) : matches;
  const hiddenCount = matches.length - shown.length;
  // Behind the "N more" row until expanded, so the catalogue is seen first.
  return { visible: hiddenCount > 0 ? shown : [...shown, ...pinned], hiddenCount };
}

export interface SearchSelectProps {
  /** Uppercase mono label above the control. Omit when the trigger names itself. */
  label?: string;
  /** Required when there is no visible label, e.g. the unit picker inside "Miktar". */
  ariaLabel?: string;
  items: readonly SearchSelectItem[];
  value: string;
  onChange: (id: string) => void;
  placeholder: string;
  requiredMark?: boolean;
  disabled?: boolean;
  /**
   * Rows shown before the "N more" row. Omit to show every item — only the
   * product picker truncates, because only it runs to 33 rows.
   */
  initialCount?: number;
  /** Defaults to on above 8 items; a search box over three units is clutter. */
  searchable?: boolean;
  /** Which edge the panel is pinned to. Right keeps a narrow trigger on screen. */
  align?: 'left' | 'right';
  /**
   * 'field' — the FormField-shaped control used in forms;
   * 'chip'  — a pill matching FilterChip, for a trigger that sits in a chip row.
   */
  variant?: 'field' | 'chip';
  /**
   * Portal the panel into this element and place it under the trigger by
   * measurement, instead of hanging it off the trigger in flow.
   *
   * Only needed when an ancestor clips the panel away. The Kesfet product menu
   * is the case: its trigger rides in the horizontally scrolling category row,
   * and `overflow-x: auto` there clips the panel's overflow on BOTH axes, so a
   * panel rendered in place is cut off at the row's bottom edge.
   */
  anchorTo?: RefObject<HTMLElement | null>;
  /** X on the trigger that clears the value. A required field has nothing to clear to. */
  clearable?: boolean;
  /**
   * Render the trigger yourself, instead of the built-in field or pill.
   *
   * Keşfet's subcategory menu hangs off the category chip the reader tapped, so
   * the trigger is a FilterChip this component does not own. Spread the given
   * props onto a button: they carry the ref the panel is measured from, the
   * open/close handler, and the combobox state screen readers need.
   */
  renderTrigger?: (props: SearchSelectTriggerProps) => ReactNode;
  /**
   * Come up already open. For a trigger that does not announce itself — a chip
   * whose only hint is a chevron — opening on the tap that created it is what
   * tells the reader the filter is there at all.
   */
  defaultOpen?: boolean;
}

/** What `renderTrigger` must put on its button for the menu to work. */
export interface SearchSelectTriggerProps {
  /**
   * A callback ref rather than the ref object itself: handing out the object
   * invites a caller to read `.current` while rendering, and the panel's
   * placement is the only thing that needs the node.
   */
  ref: (node: HTMLButtonElement | null) => void;
  onClick: () => void;
  disabled?: boolean;
  'aria-haspopup': 'listbox';
  'aria-expanded': boolean;
}

const SEARCHABLE_FROM = 8;

/**
 * Kept on screen when the anchored panel is placed under a trigger near the
 * host's right edge: past this point the panel stops tracking the trigger and
 * holds still, which reads better than a panel squeezed to a sliver.
 */
const ANCHORED_PANEL_MIN_PX = 220;

/**
 * Labeled picker with type-to-filter, in the FormField visual language.
 *
 * WHY NOT A NATIVE <select>
 * iOS and Android hand the option list to the OS, which offers no way to type:
 * with 33 products in Sebzeler the only route to a product is scrolling past
 * every product before it. Desktop lets you type but never says so, and draws
 * a cramped unstyled list. So the search box is the feature, and it is visible.
 *
 * The cost of leaving the native control is that the listbox role, the arrow
 * keys, Escape and focus return all have to be done here.
 */
export function SearchSelect({
  label,
  ariaLabel,
  items,
  value,
  onChange,
  placeholder,
  requiredMark,
  disabled,
  initialCount,
  searchable,
  align = 'left',
  variant = 'field',
  anchorTo,
  clearable,
  renderTrigger,
  defaultOpen,
}: SearchSelectProps) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState(0);
  /** Panel offset within `anchorTo`, measured at open. Null while unanchored. */
  const [anchor, setAnchor] = useState<{ left: number; top: number } | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const setTriggerNode = useCallback((node: HTMLButtonElement | null) => {
    triggerRef.current = node;
  }, []);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const labelId = `${listId}-label`;

  const canSearch = searchable ?? items.length > SEARCHABLE_FROM;
  const selected = items.find((i) => i.id === value);
  const { visible, hiddenCount } = useMemo(
    () => visibleItems(items, query, canSearch, initialCount, expanded),
    [items, query, canSearch, initialCount, expanded]
  );

  /**
   * Where the portaled panel sits inside `anchorTo`, in that host's own
   * coordinates. A no-op when the panel is not anchored.
   */
  const measureAnchor = () => {
    const host = anchorTo?.current;
    const trigger = triggerRef.current;
    if (!host || !trigger) return;
    const hostRect = host.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    setAnchor({
      left: Math.max(
        0,
        Math.min(triggerRect.left - hostRect.left, hostRect.width - ANCHORED_PANEL_MIN_PX)
      ),
      top: triggerRect.bottom - hostRect.top + 4,
    });
  };

  /**
   * Measured before the open rather than in an effect after it: both state
   * updates batch into one render, so the panel's first paint is already in
   * position. Measuring afterwards paints it at the host's origin for a frame.
   */
  const openMenu = () => {
    measureAnchor();
    setOpen(true);
  };

  /**
   * `defaultOpen` starts the panel open on the first render, before any ref
   * exists to measure. This is a layout effect rather than a plain one so the
   * measurement lands before paint — otherwise the panel shows for a frame at
   * the host's origin instead of under its trigger.
   */
  useLayoutEffect(() => {
    if (defaultOpen) measureAnchor();
    // Mount only: re-measuring on every render would fight the dismiss-on-scroll
    // behaviour below, which deliberately does not track its trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = (returnFocus = true) => {
    setOpen(false);
    setQuery('');
    setExpanded(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const pick = (id: string) => {
    onChange(id);
    close();
  };

  const clear = () => {
    onChange('');
    close();
  };

  // The focused row is about to be removed; without this, focus lands on
  // <body> and the arrow keys stop working halfway down the list.
  const expand = () => {
    setExpanded(true);
    (canSearch ? searchRef.current : triggerRef.current)?.focus();
  };

  useEffect(() => {
    if (open && canSearch) searchRef.current?.focus();
  }, [open, canSearch]);

  // Reset the highlight whenever the visible set changes under it.
  useEffect(() => setActive(0), [query, expanded, open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      // The panel is portaled out of the root when anchored, so the root alone
      // no longer describes "inside". Without the second test, pressing an
      // option counts as an outside click, the panel unmounts on pointerdown
      // and the click that would have chosen the product never lands on it.
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  /**
   * An anchored panel is placed once and does not follow its trigger, so it is
   * dismissed the moment the trigger can move. Cheaper and steadier than a
   * tracking loop, and scrolling the row away from an open menu reads as
   * leaving it anyway.
   */
  useEffect(() => {
    if (!open || !anchorTo?.current) return;
    const dismiss = (e: Event) => {
      // The options list scrolls too, and closing on that would make the list
      // unusable past its sixth row.
      if (e.type === 'scroll' && panelRef.current?.contains(e.target as Node)) return;
      close(false);
    };
    window.addEventListener('resize', dismiss);
    // Capture: scroll does not bubble, and the element that scrolls here is an
    // ancestor of the trigger rather than the document.
    document.addEventListener('scroll', dismiss, true);
    return () => {
      window.removeEventListener('resize', dismiss);
      document.removeEventListener('scroll', dismiss, true);
    };
  }, [open, anchorTo]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      if (!visible.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i + step + visible.length) % visible.length);
      return;
    }
    if (e.key === 'Enter' && open) {
      e.preventDefault();
      const item = visible[active];
      if (item) pick(item.id);
    }
  };

  /**
   * An anchored panel is portaled to the host element so no scrolling ancestor
   * can clip it. Keydown still reaches the root's handler either way: React
   * events bubble through the React tree, which a portal does not leave.
   */
  const renderPanel = (panel: ReactElement) =>
    anchorTo?.current ? createPortal(panel, anchorTo.current) : panel;

  const showClear = Boolean(clearable && selected && !disabled);
  const display = selected ? (
    <>
      {selected.icon}
      {selected.icon ? ' ' : ''}
      {selected.name}
    </>
  ) : (
    placeholder
  );

  return (
    <div
      className={`${styles.field} ${
        variant === 'chip' ? styles.fieldChip : ''
      } ${renderTrigger ? styles.fieldBare : ''}`}
      ref={rootRef}
      onKeyDown={onKeyDown}
    >
      {label && (
        <div className={styles.labelRow}>
          <span className={styles.label} id={labelId}>
            {label}
            {requiredMark && (
              <span className={styles.requiredMark} aria-hidden>
                *
              </span>
            )}
          </span>
        </div>
      )}

      {renderTrigger ? (
        /* eslint-disable-next-line react-hooks/refs --
           The rule sees a ref-shaped value crossing into a call made during
           render. Nothing here reads `.current`: `setTriggerNode` only assigns
           it, and React calls it at commit time like any other callback ref.
           The measurement that does read the node runs from an event handler
           or a layout effect, both after commit. */
        renderTrigger({
          ref: setTriggerNode,
          onClick: () => (open ? close() : openMenu()),
          disabled,
          'aria-haspopup': 'listbox',
          'aria-expanded': open,
        })
      ) : (
        /* The X is a sibling, not a child: a button inside a button is invalid
           markup, and browsers disagree about which one a click belongs to. */
        <div
          className={`${styles.triggerRow} ${
            selected && variant === 'chip' ? styles.triggerRowChipFilled : ''
          }`}
        >
          <button
            type="button"
            ref={triggerRef}
            className={`${styles.trigger} ${
              variant === 'chip' ? styles.triggerChip : ''
            } ${selected && variant === 'chip' ? styles.triggerChipFilled : ''} ${
              // A pill has no straight edge for the panel to join, and an anchored
              // panel does not touch its trigger — so neither squares off.
              open && variant === 'field' && !anchorTo ? styles.triggerOpen : ''
            } ${showClear ? styles.triggerHasClear : ''}`}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={label ? undefined : ariaLabel}
            aria-labelledby={label ? labelId : undefined}
            onClick={() => (open ? close() : openMenu())}
          >
            <span className={selected ? styles.value : styles.placeholder}>{display}</span>
            <ChevronDown
              className={`${styles.chevron} ${open ? styles.chevronUp : ''}`}
              aria-hidden
            />
          </button>
          {showClear && (
            <button
              type="button"
              className={styles.clear}
              aria-label={tr.searchSelect.clear}
              onClick={clear}
            >
              <X aria-hidden />
            </button>
          )}
        </div>
      )}

      {open &&
        renderPanel(
          <div
            ref={panelRef}
            className={`${styles.panel} ${align === 'right' ? styles.panelRight : ''} ${
              anchor ? styles.panelAnchored : ''
            }`}
            style={
              anchor
                ? ({
                    '--anchor-left': `${anchor.left}px`,
                    '--anchor-top': `${anchor.top}px`,
                  } as React.CSSProperties)
                : undefined
            }
          >
            {canSearch && (
              <div className={styles.searchBox}>
                <Search className={styles.searchIcon} aria-hidden />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={tr.searchSelect.searchPlaceholder}
                  aria-label={tr.searchSelect.searchPlaceholder}
                  aria-controls={listId}
                />
              </div>
            )}

            {visible.length === 0 ? (
              <p className={styles.empty}>{tr.searchSelect.noResults}</p>
            ) : (
              <ul
                className={styles.list}
                id={listId}
                role="listbox"
                aria-label={label ?? ariaLabel}
              >
                {visible.map((item, i) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={item.id === value}
                      className={`${styles.option} ${i === active ? styles.active : ''} ${
                        item.id === value ? styles.selected : ''
                      }`}
                      onPointerEnter={() => setActive(i)}
                      onClick={() => pick(item.id)}
                    >
                      {item.icon && <span aria-hidden>{item.icon}</span>}
                      {/* Wrapped rather than bare: text-overflow needs an element
                          of its own to ellipsis, which an anonymous flex item in
                          this row would not give it. */}
                      <span className={styles.optionLabel}>{item.name}</span>
                    </button>
                  </li>
                ))}
                {/* Inside the scrolling list, not under it: below the list it
                    would take its own height with it and shrink the panel by a
                    row at the very moment of expanding. */}
                {hiddenCount > 0 && (
                  <li role="presentation">
                    <button type="button" className={styles.more} onClick={expand}>
                      {tr.searchSelect.more(hiddenCount)}
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
    </div>
  );
}
